import { DocumentFileList } from "@/common/components/DocumentFileList";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader";
import Skeleton from "@/common/components/Skeleton";
import useBlobs from "@/common/hooks/queries/useBlobs";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { FormTransformers } from "@/common/utils/formTransformers";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { ModalDeleteAdditionalDocument } from "@/features/privateCompanies/components/showBranch/modalDeleteAdditionalDocument";
import { useCreateCompanyBranchAdditionalDocument } from "@/features/privateCompanies/hooks/branches/useCreateCompanyBranchAdditionalDocument";
import { UpdateAdditionalDocumentsValidationSchema } from "@/features/privateCompanies/schemas/updateAdditionalDocuments.schema";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CompanyBranchData } from "../../types/companyBranches.type";

interface AdditionalDocumentsProps {
  branchId?: string
  additionalDocuments: CompanyBranchData['additional_documents']
  companyBranchUpdatedAt?: number
}

export default function AdditionalDocuments({
  branchId,
  additionalDocuments,
  companyBranchUpdatedAt,
}: AdditionalDocumentsProps) {
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [attachmentId, setAttachmentId] = useState<string>()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const hasPermissionToUpdateAdditionalDocuments = hasUserPermission(
    'Admin::Client::CompanyBranches::UpdateAdditionalDocuments',
  )

  const { control, setError, clearErrors, reset } = useForm<UpdateAdditionalDocumentsValidationSchema>({
    defaultValues: {
      company_branch: {
        additional_documents_attachments_attributes: additionalDocuments.map((document) => ({
          id: document.attachment_id,
          attachment_id: document.attachment_id,
          fileName: getFilenameFromUrl(document?.url),
          currentFileUrl: document?.url,
        })),
      },
    },
  })

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs({
    onSuccess: (response) => {
      createCompanyBranchAdditionalDocument(response.data.data.id)
    },
  })

  const { mutate: createCompanyBranchAdditionalDocument, isPending: createCompanyBranchAdditionalDocumentIsPending } =
    useCreateCompanyBranchAdditionalDocument({
      branchId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['companyBranch', branchId] })
      },
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  useEffect(() => {
    reset({
      company_branch: {
        additional_documents_attachments_attributes: additionalDocuments.map((document) => ({
          id: document.attachment_id,
          attachment_id: document.attachment_id,
          fileName: getFilenameFromUrl(document?.url),
          currentFileUrl: document?.url,
        })),
      },
    })
  }, [companyBranchUpdatedAt])

  return (
    <div className="flex flex-col gap-4">
      <p className="font-bold text-lg">
        Anexos
      </p>

      {isUserPermissionsPending ? (
        <div className="flex gap-x-2">
          <Skeleton className="h-[100px] w-[180px]" />
          <Skeleton className="h-[100px] w-[180px]" />
        </div>
      ) : !!hasPermissionToUpdateAdditionalDocuments ? (
        <>
          <MultipleDocumentUploader
            title="Anexe documentos"
            control={control}
            name="company_branch.additional_documents_attachments_attributes"
            fieldName="company_branch.additional_documents_attachments_attributes"
            uploadBlob={uploadBlob}
            accept=".pdf"
            containerClassName="flex-row-reverse justify-end"
            disabled={uploadBlobIsPending || createCompanyBranchAdditionalDocumentIsPending}
            softDelete={setAttachmentId}
            fieldKey="attachment_id"
            setError={(message) => {
              if (message) {
                setError('company_branch.additional_documents_attachments_attributes', { message })
              } else {
                clearErrors('company_branch.additional_documents_attachments_attributes')
              }
            }}
          />

          <ModalDeleteAdditionalDocument attachmentId={attachmentId} setAttachmentId={setAttachmentId} />

          <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
        </>
      ) : (
        <DocumentFileList
          files={
            additionalDocuments.map((document) => ({
              id: document.attachment_id,
              fileName: getFilenameFromUrl(document?.url),
              currentFileUrl: document?.url,
            })) || []
          }
          dataTestid="additional_documents"
        />
      )}
    </div>
  )
}
